import { Link } from 'react-router-dom';
import { BarChart3, ChevronRight, LayoutGrid, ShieldAlert, ShieldCheck } from 'lucide-react';
import type { FleetInsightsDoc, FleetOverview } from '../../services/fleetClientService';
import { GradeSpectrum } from './FleetViz';
import type { PolicyCompliance } from '../../services/policyService';

export function PolicyCompliancePanel({
  compliance,
  orgId,
}: {
  compliance: PolicyCompliance;
  orgId: string;
}) {
  const { summary, repos } = compliance;
  const clean = summary.reposInViolation === 0;
  return (
    <section
      className={`p-5 rounded-2xl border shadow-sm ${
        clean
          ? 'border-green-200/70 dark:border-green-900/60 bg-green-50/50 dark:bg-green-950/20'
          : 'border-amber-200/70 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${clean ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'}`}
          >
            <ShieldCheck
              className={`w-4 h-4 ${clean ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}
            />
          </span>
          <h2 className="font-semibold text-gray-900 dark:text-white">Policy compliance</h2>
        </div>
        <Link
          to={`/orgs/${orgId}/insights`}
          className="text-xs text-teal-700 dark:text-teal-400 hover:underline"
        >
          Manage policies →
        </Link>
      </div>
      {summary.policyCount === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No policies set. Define required/prohibited practices to govern the fleet.
        </p>
      ) : (
        <>
          <div className="flex items-baseline gap-6 mb-3">
            <div>
              <div
                className={`text-3xl font-bold tracking-tight tabular-nums ${clean ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}
              >
                {summary.reposInViolation}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">repos out of policy</div>
            </div>
            <div>
              <div className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
                {summary.totalViolations.toLocaleString()}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                violations across {summary.policyCount} policies
              </div>
            </div>
          </div>
          {repos.slice(0, 5).map((r) => (
            <div key={r.id} className="text-xs text-gray-600 dark:text-gray-400">
              <Link
                to={`/repository/${r.id}`}
                className="text-teal-700 dark:text-teal-400 hover:underline"
              >
                {r.name}
              </Link>{' '}
              — {r.violations.map((v) => v.entityName).join(', ')}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

export function SecurityPosture({ insights }: { insights: FleetInsightsDoc }) {
  const p = insights.security_posture;
  if (!p) return null;
  const pct = p.fleetSize ? Math.round((p.reposWithSecurityGap / p.fleetSize) * 100) : 0;
  return (
    <section className="p-5 rounded-2xl border border-red-200/70 dark:border-red-900/60 bg-red-50/50 dark:bg-red-950/20 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
          <ShieldAlert className="w-4 h-4 text-red-600 dark:text-red-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Security posture</h2>
      </div>
      <div className="flex items-baseline gap-6">
        <div>
          <div className="text-3xl font-bold tracking-tight tabular-nums text-red-600 dark:text-red-400">
            {p.reposWithSecurityGap}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            of {p.fleetSize} repos have a security gap ({pct}%)
          </div>
        </div>
        <div>
          <div className="text-3xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
            {p.highSeverityFindings.toLocaleString()}
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            high/critical findings fleet-wide
          </div>
        </div>
      </div>
    </section>
  );
}

export function ScoreDistribution({ overview }: { overview: FleetOverview }) {
  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-100 dark:bg-teal-900/30">
          <BarChart3 className="w-4 h-4 text-teal-600 dark:text-teal-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Fleet health</h2>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {overview.analyzedCount}/{overview.repoCount} repos analyzed · mean score{' '}
        {overview.scoreDistribution.mean ?? '—'} · {overview.planTotals.automatableItems}{' '}
        automatable fixes identified
      </p>
      <GradeSpectrum buckets={overview.scoreDistribution.buckets} height={14} />
    </section>
  );
}

function AdoptionBar({
  orgId,
  slug,
  name,
  pct,
  count,
  fleetSize,
}: {
  orgId: string;
  slug: string;
  name: string;
  pct: number;
  count: number;
  fleetSize: number;
}) {
  return (
    <Link
      to={`/orgs/${orgId}/practice/${encodeURIComponent(slug)}?name=${encodeURIComponent(name)}`}
      className="w-full flex items-center gap-3 group rounded-lg px-1.5 py-1 -mx-1.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40"
    >
      <span className="w-44 shrink-0 text-xs text-left text-gray-700 dark:text-gray-300 truncate flex items-center gap-1 group-hover:text-teal-700 dark:group-hover:text-teal-400">
        <ChevronRight className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" />
        {name}
      </span>
      <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct >= 0.7 ? 'bg-green-500' : pct >= 0.3 ? 'bg-yellow-400' : 'bg-red-400'}`}
          style={{ width: `${Math.max(2, pct * 100)}%` }}
        />
      </div>
      <span className="w-14 shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
        {count}/{fleetSize}
      </span>
    </Link>
  );
}

export function AdoptionHeatmap({
  insights,
  orgId,
}: {
  insights: FleetInsightsDoc;
  orgId: string;
}) {
  const entries = Object.entries(insights.adoption)
    .filter(([, a]) => a.kind === 'tool' || a.kind === 'practice')
    .slice(0, 12);
  return (
    <section className="p-5 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2.5 mb-1">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
          <LayoutGrid className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </span>
        <h2 className="font-semibold text-gray-900 dark:text-white">Practice adoption</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Click a practice to see which repositories use it.
      </p>
      <div className="space-y-2">
        {entries.map(([slug, a]) => (
          <AdoptionBar
            key={slug}
            orgId={orgId}
            slug={slug}
            name={a.name}
            pct={a.pct}
            count={a.count}
            fleetSize={insights.fleet_size}
          />
        ))}
      </div>
    </section>
  );
}
